import React from "react";
import moment from "moment";
const _ = require("underscore");

/****
  Name: CalculateActiveMetrics
  Definition:
    Calculate usage and count active sessions.
  Found in files:
    Home.js
****/
export function CalculateActiveMetrics(sessions) {
  let activeConsumption = 0;
  _.each(sessions, function (session) {
    if (session.current_kw !== null)
      activeConsumption += parseFloat(session.current_kw);
  });

  let consumptionUnit = "kW";
  if (activeConsumption > 1000) {
    activeConsumption /= 1000;
    consumptionUnit = "MW";
  }

  return {
    activeConsumption: activeConsumption.toFixed(2),
    activeConsumptionUnit: consumptionUnit,
    activeSessions: sessions.length,
  };
}

/****
  Name: CalculateEnergyConsumption
  Definition:
    Define array of energy consumed by date.
  Found in files:
    Home.js
****/
export function CalculateEnergyConsumption(sessions) {
  var energyPerDateArray = [];
  _.each(sessions, function (session) {
    energyPerDateArray.push({
      x: moment(session.date_start).format("MM/DD"),
      y: parseInt(session.energy.toFixed(0)),
      dateObject: moment(session.date_start).format("MMM DD YYYY"),
      year: parseInt(moment(session.date_start).format("YYYY")),
      month: parseInt(moment(session.date_start).format("M")),
    });
  });
  return { energyPerDateArray };
}

/****
  Name: CalculateRevenue
  Definition:
    Define an array of revenue by date.
  Found in files:
    Home.js
****/
export function CalculateRevenue(records) {
  var revenuePerDateArray = [];
  var recordsArray = [];
  _.each(records, function (record) {
    recordsArray.push({
      shorthand_date: moment(record.created_at).startOf("day").format("MM/DD/YYYY"),
      ...record,
    });
  });

  let dateArray = _.groupBy(recordsArray, "shorthand_date");
  _.each(dateArray, function (date) {
    let bill = 0;
    _.each(date, function (record) { bill += parseFloat(record.total_bill) });
    revenuePerDateArray.push({
      x: moment(date[0].created_at).format("MM/DD"),
      y: parseFloat(bill).toFixed(2),
      dateObject: moment(date[0].created_at).format("MMM DD YYYY"),
      year: parseInt(moment(date[0].created_at).format("YYYY")),
      month: parseInt(moment(date[0].created_at).format("M")),
    });
  });
  return { revenuePerDateArray };
}

/****
  Name: CalculateTotalSessions
  Definition:
    Define an array of total sessions by date.
  Found in files:
    Home.js
****/
export function CalculateTotalSessions(sessions) {
  var sessionsPerDateArray = [];
  _.each(sessions, function (session) {
    sessionsPerDateArray.push({
      x: moment(session.date_start).format("MM/DD"),
      y: parseInt(session.sessions),
      dateObject: moment(session.date_start).format("MMM DD YYYY"),
      year: parseInt(moment(session.date_start).format("YYYY")),
      month: parseInt(moment(session.date_start).format("M")),
    });
  });
  return { sessionsPerDateArray };
}

/****
  Name: CalculateUtilization
  Definition:
    Define an array of utilization percentages by date.
  Found in files:
    Home.js
****/
export function CalculateUtilization(sessions) {
  var utilizationPerDateArray = [];
  _.each(sessions, function (session) {
    utilizationPerDateArray.push({
      x: moment(session.date_start).format("MM/DD"),
      y: parseFloat(session.util_pct),
      dateObject: moment(session.date_start).format("MMM DD YYYY"),
      year: parseInt(moment(session.date_start).format("YYYY")),
      month: parseInt(moment(session.date_start).format("M")),
    });
  });
  return { utilizationPerDateArray };
}

/****
  Name: GetSessions
  Definition:
    Group sessions into arrays by day and count metrics for each day.
  Found in files:
    Home.js
****/
export function GetSessions(sessions, stationIds) {
  const unique = [...new Set(stationIds.map((item) => item.station_id))];
  let sessionArray = [];
  // Remove sessions outside of COD and organize by date_start
  let sessionGroups = _.groupBy(
    _.filter(sessions, (session) => {
      const stats = _.where(stationIds, { station_id: session.station_id });
      return stats.length === 0 ||
        (stats.length > 0 &&
          (stats[0].cod === null ||
            moment(session.date_start).isAfter(moment(stats[0].cod))));
    }),
    "date_start"
  );
  _.each(
    _.sortBy(_.toArray(sessionGroups), function (num) {
      return num;
    }).reverse(),
    function (session) {
      let sessionsCount = 0;
      let energy = 0;
      let percent = 0;
      // Sum up total of sessions, energy, and utilization percentage
      for (let i = 0; i < session.length; i++) {
        // Sum up values
        sessionsCount += parseInt(session[i].sessions);
        energy += parseFloat(session[i].energy);
        percent += session[i].util_pct;
      }
      percent /= unique.length;
      // Push the date's values
      sessionArray.push({
        date_start: session[0].date_start,
        sessions: sessionsCount,
        energy,
        util_pct: percent * 100,
      });
    }
  );

  sessionArray = _.sortBy(sessionArray, "date_start");
  return { sessionArray };
}

/****
  Name: StationNumbers
  Defintion:
    Sum of each type of station (available, inUse, unreachable/faulted),
    plus loading boolean to show values.
  Found in files:
    Station.js
****/
export function StationNumbers(stations, sessions) {

  let opStations = stations;
  let totalStations = [];
  let availableStations = [];
  let inUseStations = [];
  let unreachableStations = [];

  // Filter unoperational stations if COD key exists
  if (
    stations.filter((station) => {
      return station.hasOwnProperty("cod");
    }).length > 0
  )
    opStations = stations.filter((station) => {
      return station.cod !== null && moment(station.cod).isBefore();
    });

  // Check status and add to total for each station
  _.each(opStations, (station) => {
    try {
      if(sessions.length > 0) {
      let activeSessionStations = _.pluck(sessions, 'station_id')
      let activeSessionIndex = activeSessionStations.indexOf(station.station_id)
      if(activeSessionIndex !== -1) {
        station = {
          ...station,
          vehicle_soc: sessions[activeSessionIndex].vehicle_soc,
          energy: sessions[activeSessionIndex].energy,
          current_kw: sessions[activeSessionIndex].current_kw
        }
      }
    }}
    catch {
    }
    totalStations.push(station)
    if (
      station.port_1_status === "AVAILABLE" ||
      station.port_2_status === "AVAILABLE"
    ) {
      availableStations.push(station);
    }
    if (
      station.port_1_status === "INUSE" ||
      station.port_1_status === "CHARGING" ||
      station.port_1_status === "SUSPENDEDEV" ||
      station.port_2_status === "INUSE" ||
      station.port_2_status === "CHARGING" ||
      station.port_2_status === "SUSPENDEDEV"
    ) {
      inUseStations.push(station);
    }
    if (
      station.port_1_status === "COMING SOON" ||
      station.port_1_status === "UNREACHABLE" ||
      station.port_1_status === "UNKNOWN" ||
      station.port_1_status === "FAULTED" ||
      station.port_1_status === "NEEDS SERVICE" ||
      station.port_2_status === "COMING SOON" ||
      station.port_2_status === "UNREACHABLE" ||
      station.port_2_status === "UNKNOWN" ||
      station.port_2_status === "FAULTED" ||
      station.port_2_status === "NEEDS SERVICE"
    ) {
      unreachableStations.push(station);
    }
  });

  // Count ports
  const totalPort1s = totalStations.filter(
    (station) => station.port_1_status !== ""
  ).length;
  const totalPort2s = totalStations.filter(
    (station) =>
      station.port_2_status !== "" && station.port_2_status !== undefined
  ).length;
  const availablePort1s = availableStations.filter(
    (station) => station.port_1_status === "AVAILABLE"
  ).length;
  const availablePort2s = availableStations.filter(
    (station) =>
      station.port_2_status === "AVAILABLE" &&
      station.port_2_status !== undefined
  ).length;
  const inUsePort1s =
    inUseStations.filter((station) =>
      ["INUSE", "CHARGING", "SUSPENDEDEV"].includes(station.port_1_status)
    ).length || 0;
  const inUsePort2s =
    inUseStations.filter(
      (station) =>
        ["INUSE", "CHARGING", "SUSPENDEDEV"].includes(station.port_2_status) &&
        station.port_2_status !== undefined
    ).length || 0;
  const unreachablePort1s = unreachableStations.filter(
    (station) =>
      !["AVAILABLE", "INUSE", "CHARGING", "SUSPENDEDEV", ""].includes(station.port_1_status)
  ).length;
  const unreachablePort2s = unreachableStations.filter(
    (station) =>
      !["AVAILABLE", "INUSE", "CHARGING", "SUSPENDEDEV", ""].includes(station.port_2_status) &&
      station.port_2_status !== undefined
  ).length;

  return {
    totalStations,
    availableStations,
    inUseStations,
    unreachableStations,
    totalPorts: totalPort1s + totalPort2s,
    availablePorts: availablePort1s + availablePort2s,
    inUsePorts: inUsePort1s + inUsePort2s,
    unreachablePorts: unreachablePort1s + unreachablePort2s,
  };
}